import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DomainAddIcon from '@mui/icons-material/DomainAdd';
import CategoryIcon from '@mui/icons-material/Category';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';
import type { SvgIconProps } from '@mui/material';
import { Routes } from '../../../routes';
import type { OnboardingStatusResponse } from '../api/onboardingApi';

export type OnboardingStepKey =
  | 'invite'
  | 'import-site'
  | 'assign-sites'
  | 'labour-groups'
  | 'assign-groups';

export interface OnboardingStepDef {
  key: OnboardingStepKey;
  title: string;
  icon: React.ComponentType<SvgIconProps>;
  isComplete: (status: OnboardingStatusResponse) => boolean;
  ctaLabel: string;
  ctaPath: string;
}

export const ONBOARDING_STEPS: readonly OnboardingStepDef[] = [
  {
    key: 'invite',
    title: 'Invite your team',
    icon: PersonAddIcon,
    isComplete: s => s.invitedUserCount >= 1 || s.userCount > 1,
    ctaLabel: 'Invite Team',
    ctaPath: `${Routes.users.manage}?panel=invite&id=new`,
  },
  {
    key: 'import-site',
    title: 'Import a site',
    icon: DomainAddIcon,
    isComplete: s => s.siteCount > 0,
    ctaLabel: 'Import Site',
    ctaPath: Routes.sites.import,
  },
  {
    key: 'assign-sites',
    title: 'Assign users to sites',
    icon: PersonPinCircleIcon,
    isComplete: s => s.siteAssignmentCount > 0,
    ctaLabel: 'Manage Users',
    ctaPath: Routes.users.manage,
  },
  {
    key: 'labour-groups',
    title: 'Set up labour groups',
    icon: CategoryIcon,
    isComplete: s => s.labourGroupCount > 0,
    ctaLabel: 'Manage Labour',
    ctaPath: `${Routes.labour.manage}?tab=groups`,
  },
  {
    key: 'assign-groups',
    title: 'Assign workers to labour groups',
    icon: GroupAddIcon,
    isComplete: s => s.assignmentCount > 0,
    ctaLabel: 'Manage Users',
    ctaPath: Routes.users.manage,
  },
] as const;

export const TOTAL_STEPS = ONBOARDING_STEPS.length;

export function getCompletedCount(status: OnboardingStatusResponse): number {
  return ONBOARDING_STEPS.filter(step => step.isComplete(status)).length;
}

export function isAllComplete(status: OnboardingStatusResponse): boolean {
  return getCompletedCount(status) === TOTAL_STEPS;
}

export function getNextStep(
  status: OnboardingStatusResponse
): OnboardingStepDef | null {
  return ONBOARDING_STEPS.find(step => !step.isComplete(status)) ?? null;
}

export function getLastCompletedStep(
  status: OnboardingStatusResponse
): OnboardingStepDef | null {
  const completed = ONBOARDING_STEPS.filter(step => step.isComplete(status));
  return completed.length > 0 ? completed[completed.length - 1] : null;
}
